// File: src/pages/EditAddProduct.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Form,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { MdCancel } from "react-icons/md";
import { FaUpload } from "react-icons/fa";
import swal from "sweetalert";

import {
  AllCategoryList,
  _Get,
  _Put,
  _GetByUrl,
  PurchaseOrderList,
} from "../import/ApiEndPoint/ApiCalling";
import {
  Image_URL,
  Update_Product,
  ViewOne_Product,
  WareahouseList_For_addProduct,
  viewUnits,
  ProductSection_List,
  WareHouse_Closing_Stock,
} from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("edit-prod-inline-css")) return;
  const s = document.createElement("style");
  s.id = "edit-prod-inline-css";
  s.textContent = `
    .ep-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .ep-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .ep-small{ font-size:12px; color:#6b7280; }
    .ep-img{ border-radius:10px; width:100%; height:110px; object-fit:cover; }
    .ep-del{ position:absolute; right:8px; top:4px; cursor:pointer; }
    .ep-btn{ background:#0b5cf6; color:#fff; font-weight:700; height:32px; }
    .ep-opening-note{
      margin-top:6px;
      padding:7px 9px;
      border-radius:10px;
      background:#fff7ed;
      border:1px solid #fed7aa;
      color:#9a3412;
      font-size:12px;
      font-weight:700;
      line-height:1.35;
    }
  `;
  document.head.appendChild(s);
};

const toB = (v) => v === true || v === "true";
const num = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));

const num0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

/* ================= Financial Year Helpers ================= */

const normalizeFinancialYear = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);
  if (!match) return raw;

  const startYear = match[1];
  const endYear = match[2].length === 4 ? match[2].slice(-2) : match[2];

  return `${startYear}-${endYear}`;
};

const getFYStartYear = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  return m >= 3 ? y : y - 1;
};

const getFinancialYearLabel = (date = new Date()) => {
  const start = getFYStartYear(date);
  return normalizeFinancialYear(`${start}-${start + 1}`);
};

const buildFinancialYearOptions = (past = 5, future = 2) => {
  const currentStart = getFYStartYear(new Date());
  const arr = [];

  for (let y = currentStart - past; y <= currentStart + future; y++) {
    arr.push(normalizeFinancialYear(`${y}-${y + 1}`));
  }

  return arr.reverse();
};

const getSelectedFinancialYear = () =>
  normalizeFinancialYear(
    localStorage.getItem("financialYearSelected") || getFinancialYearLabel(),
  );

const parseFYStart = (fy) => {
  const start = Number(String(normalizeFinancialYear(fy) || "").split("-")[0]);
  return Number.isFinite(start) ? start : getFYStartYear();
};

const getPreviousFY = (fy) => {
  const start = parseFYStart(fy);
  return normalizeFinancialYear(`${start - 1}-${String(start).slice(-2)}`);
};

const stockSnapshotKey = (db, fy) =>
  `FY_CLOSING_STOCK_SNAPSHOT:${String(db || "")}:${normalizeFinancialYear(fy)}`;

const normId = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (val._id) return String(val._id);
    if (val.$oid) return String(val.$oid);
    if (val.id) return String(val.id);
  }
  return String(val);
};

const normTitle = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getProductTitle = (p = {}) =>
  normTitle(
    p?.Product_Title ||
      p?.productId?.Product_Title ||
      p?.product?.Product_Title ||
      p?.productName ||
      p?.ProductName ||
      "",
  );

const getProductIdCandidates = (p = {}, routeId = "") =>
  Array.from(
    new Set(
      [p?._id, p?.id, p?.productId?._id, p?.productId, p?.product?._id, routeId]
        .map(normId)
        .filter(Boolean),
    ),
  );

const getWarehouseIdCandidates = (p = {}) =>
  Array.from(
    new Set(
      [
        p?.warehouse?._id,
        p?.warehouse,
        p?.warehouseId,
        p?.warehouseId1,
        p?._warehouseIdResolved,
      ]
        .map(normId)
        .filter(Boolean),
    ),
  );

const buildPreviousClosingKeys = (product = {}, routeId = "") => {
  const productIds = getProductIdCandidates(product, routeId);
  const warehouseIds = getWarehouseIdCandidates(product);
  const title = getProductTitle(product);
  const keys = [];

  warehouseIds.forEach((wid) => {
    productIds.forEach((pid) => keys.push(`${wid}::${pid}`));
    if (title) keys.push(`${wid}::title:${title}`);
  });

  productIds.forEach((pid) => keys.push(`id:${pid}`));
  if (title) keys.push(`title:${title}`);

  return Array.from(new Set(keys.filter(Boolean)));
};

const findPreviousClosingInSnapshot = (db, previousFY, product, routeId) => {
  try {
    const raw = localStorage.getItem(stockSnapshotKey(db, previousFY));
    const snap = raw ? JSON.parse(raw) : {};
    const keys = buildPreviousClosingKeys(product, routeId);

    for (const key of keys) {
      const found = snap?.[key];
      if (found) {
        return {
          qty: num0(found?.closingQty ?? found?.qty),
          rate: num0(
            found?.closingAveRate ?? found?.closingRate ?? found?.rate,
          ),
          source: `Previous FY closing snapshot (${previousFY})`,
        };
      }
    }
  } catch (e) {
    console.warn("Unable to read previous FY closing snapshot", e);
  }

  return null;
};

const normalizeClosingRowsFromResponse = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (!Array.isArray(res?.Warehouse)) return [];

  const out = [];

  res.Warehouse.forEach((wh) => {
    if (Array.isArray(wh?.productItems)) {
      wh.productItems.forEach((it, index) => {
        const productObj =
          it?.productId && typeof it.productId === "object" ? it.productId : {};

        out.push({
          ...it,
          _id: it?._id || `${wh?._id || wh?.warehouseId}-${index}`,
          warehouse:
            it?.warehouse || wh?.warehouseId || wh?._id || wh?.warehouseId1,
          warehouseId: wh?.warehouseId || wh?._id || wh?.warehouseId1,
          warehouseName: wh?.warehouseName || "",
          Product_Title:
            it?.Product_Title ||
            productObj?.Product_Title ||
            it?.productName ||
            "",
          productId: it?.productId,
        });
      });
    } else {
      out.push(wh);
    }
  });

  return out;
};

const findPreviousClosingInApiRows = (
  rows = [],
  product = {},
  routeId = "",
) => {
  const productIds = new Set(getProductIdCandidates(product, routeId));
  const warehouseIds = new Set(getWarehouseIdCandidates(product));
  const title = getProductTitle(product);

  const matched = (rows || []).find((row) => {
    const rowProductIds = getProductIdCandidates(row, "");
    const rowWarehouseIds = getWarehouseIdCandidates(row);
    const rowTitle = getProductTitle(row);

    const productMatch =
      rowProductIds.some((pid) => productIds.has(pid)) ||
      (title && rowTitle && title === rowTitle);

    if (!productMatch) return false;

    if (!warehouseIds.size) return true;

    return rowWarehouseIds.some((wid) => warehouseIds.has(wid));
  });

  if (!matched) return null;

  return {
    qty: num0(matched?.closingQty ?? matched?.cQty ?? matched?.currentStock),
    rate: num0(
      matched?.closingAveRate ??
        matched?.closingRate ??
        matched?.cRate ??
        matched?.price,
    ),
    source: "Previous FY closing API",
  };
};

// --- URL helpers ---
const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");
const trimLeadingSlash = (s) => String(s || "").replace(/^\/+/, "");
const joinUrl = (...parts) =>
  parts
    .filter(Boolean)
    .map((p, i) =>
      String(p)
        [i === 0 ? "replace" : "replaceAll"](/\/+$/g, "")
        .replace(/^\/+/g, ""),
    )
    .join("/");

export default function EditAddProduct() {
  useEffect(() => injectOnce(), []);

  const navigate = useNavigate();
  const { id } = useParams();

  const currentFY = useMemo(() => getFinancialYearLabel(), []);
  const fyOptions = useMemo(() => buildFinancialYearOptions(5, 2), []);

  // ===== Data sources =====
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [units, setUnits] = useState([]);
  const [sections, setSections] = useState([]);

  // ===== Images =====
  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);

  // ===== Form =====
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [prod, setProd] = useState({
    database: "",
    openingFinancialYear: currentFY,
    category: "",
    SubCategory: "",
    section: "",
    warehouse: "",
    purchaseStatus: "",
    Product_Title: "",
    HSN_Code: "",
    GSTRate: "",
    Product_Desc: "",
    MIN_stockalert: "",
    primaryUnit: "",
    secondaryUnit: "",
    secondarySize: "",
    Opening_Stock: "",
    openingRate: "",
    Purchase_Rate: "",
    Product_MRP: "",
    landedCost: "",
    status: "",
  });

  // This always stores the product's real saved opening data.
  // When the selected FY is different, the UI displays previous FY closing,
  // but submit still saves these original values.
  const [savedOpening, setSavedOpening] = useState({
    openingFinancialYear: currentFY,
    Opening_Stock: "",
    openingRate: "",
  });

  const [previousYearOpening, setPreviousYearOpening] = useState({
    loading: false,
    qty: "",
    rate: "",
    source: "",
    previousFY: "",
  });

  const [lastPurchasePrice, setLastPurchasePrice] = useState("");
  const [lastPartyName, setLastPartyName] = useState("");

  const onProd = (name, value) => setProd((p) => ({ ...p, [name]: value }));

  // ===== Initial load =====
  useEffect(() => {
    const u = getUser();

    const load = async () => {
      try {
        const resProd = await _Get(ViewOne_Product, id);
        const p = resProd?.Product || {};

        const basicPrice = p?.price ?? p?.basicPrice ?? p?.Purchase_Rate ?? "";

        const realSavedOpening = {
          openingFinancialYear: normalizeFinancialYear(
            p?.openingFinancialYear || currentFY,
          ),
          Opening_Stock: p?.Opening_Stock ?? "",
          openingRate: p?.openingRate ?? "",
        };

        setSavedOpening(realSavedOpening);

        setProd((prev) => ({
          ...prev,
          database: u?.database || "",
          openingFinancialYear: realSavedOpening.openingFinancialYear,
          category: p?.category || "",
          SubCategory: p?.SubCategory || "",
          section: "",
          warehouse: p?.warehouse || "",
          purchaseStatus: String(Boolean(p?.igstType)),
          Product_Title: p?.Product_Title || "",
          HSN_Code: p?.HSN_Code || "",
          GSTRate: p?.GSTRate ?? "",
          Product_Desc: p?.Product_Desc ?? "",
          MIN_stockalert: p?.MIN_stockalert ?? "",
          primaryUnit: p?.primaryUnit ?? "",
          secondaryUnit: p?.secondaryUnit ?? "",
          secondarySize: p?.secondarySize ?? "",
          Opening_Stock: realSavedOpening.Opening_Stock,
          openingRate: realSavedOpening.openingRate,
          Purchase_Rate: basicPrice,
          Product_MRP: p?.Product_MRP ?? p?.mrp ?? "",
          landedCost: p?.landedCost ?? "",
          status: p?.status || "Active",
        }));

        setExistingImages(
          Array.isArray(p?.Product_image) ? p.Product_image : [],
        );

        let fetchedSections = [];
        try {
          const secUrl = joinUrl(
            Image_URL,
            ProductSection_List,
            u?._id,
            u?.database,
          );

          const resSec = await fetch(secUrl, {
            headers: { Accept: "application/json" },
          });

          if (!resSec.ok) throw new Error("HTTP " + resSec.status);

          const json = await resSec.json();
          fetchedSections = Array.isArray(json?.ProductSection)
            ? json.ProductSection
            : [];
          setSections(fetchedSections);
        } catch (e) {
          console.error("Failed to load Product Sections", e);
          setSections([]);
        }

        const sectionIdFromServer =
          p?.productSection || p?.Product_Section || "";

        let selectedSectionId = "";

        if (sectionIdFromServer) {
          if (
            sectionIdFromServer === "RAW_MATERIAL" ||
            sectionIdFromServer === "RAW MATERIAL"
          ) {
            selectedSectionId = "RAW_MATERIAL";
          } else {
            const byId = fetchedSections.find(
              (s) => s?._id === sectionIdFromServer,
            );
            if (byId) selectedSectionId = byId._id;
            else {
              const byName = fetchedSections.find(
                (s) => s?.name === sectionIdFromServer,
              );
              if (byName) selectedSectionId = byName._id;
            }
          }
        }

        if (selectedSectionId) {
          setProd((prev) => ({ ...prev, section: selectedSectionId }));
        }

        const [resWh, resCat, resUnits] = await Promise.all([
          _Get(WareahouseList_For_addProduct, u?.database),
          AllCategoryList(u?._id, u?.database),
          _GetByUrl(viewUnits, u?.database),
        ]);

        setWarehouses(Array.isArray(resWh?.Warehouse) ? resWh.Warehouse : []);

        const cats = Array.isArray(resCat?.Category) ? resCat.Category : [];
        setCategories(cats);

        const matchCat = cats.find((c) => c?.name === (p?.category || ""));
        setSubcats(
          Array.isArray(matchCat?.subcategories) ? matchCat.subcategories : [],
        );

        setUnits(
          Array.isArray(resUnits?.dynamicUnit?.Units)
            ? resUnits.dynamicUnit.Units
            : [],
        );

        try {
          const orders = await PurchaseOrderList(u?._id, u?.database);
          const list = Array.isArray(orders?.orderHistory)
            ? orders.orderHistory
            : [];

          list.sort((a, b) => {
            const da = new Date(a?.date || a?.createdAt || 0).getTime();
            const db = new Date(b?.date || b?.createdAt || 0).getTime();
            return db - da;
          });

          const productIdStr = String(p?._id || id || "");

          const grabId = (x) =>
            String(
              x?.productId?._id ??
                x?.productId?.$oid ??
                x?.productId?.id ??
                x?.productId ??
                "",
            );

          let foundPrice = "";
          let foundParty = "";

          for (const ord of list) {
            if (!Array.isArray(ord?.orderItems)) continue;

            const match = ord.orderItems.find(
              (it) => grabId(it) === productIdStr,
            );

            if (match) {
              foundPrice =
                match?.price != null
                  ? String(match.price)
                  : match?.basicPrice != null
                    ? String(match.basicPrice)
                    : "";

              foundParty = String(ord?.fullName || "");
              break;
            }
          }

          setLastPurchasePrice(foundPrice);
          setLastPartyName(foundParty);
        } catch (e) {
          console.warn("Last purchase info not available:", e);
          setLastPurchasePrice("");
          setLastPartyName("");
        }
      } catch (err) {
        console.error(err);
        swal("Error", "Failed to load product", "error");
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const selectedFinancialYear = useMemo(() => getSelectedFinancialYear(), []);

  const normalizedSelectedFY = normalizeFinancialYear(selectedFinancialYear);
  const normalizedProductOpeningFY = normalizeFinancialYear(
    savedOpening.openingFinancialYear || prod.openingFinancialYear,
  );

  const isOpeningYearMismatch =
    Boolean(normalizedSelectedFY) &&
    Boolean(normalizedProductOpeningFY) &&
    normalizedSelectedFY !== normalizedProductOpeningFY;

  const displayOpeningFinancialYear = isOpeningYearMismatch
    ? normalizedSelectedFY
    : prod.openingFinancialYear;

  const displayOpeningStock = isOpeningYearMismatch
    ? previousYearOpening.qty
    : prod.Opening_Stock;

  const displayOpeningRate = isOpeningYearMismatch
    ? previousYearOpening.rate
    : prod.openingRate;

  const displayOpeningTotal = (
    num0(displayOpeningStock) * num0(displayOpeningRate)
  ).toFixed(2);

  const openingFieldsReadOnly = Boolean(isOpeningYearMismatch);

  useEffect(() => {
    const loadPreviousOpening = async () => {
      if (!isOpeningYearMismatch) {
        setPreviousYearOpening({
          loading: false,
          qty: "",
          rate: "",
          source: "",
          previousFY: "",
        });
        return;
      }

      if (!prod?.Product_Title && !id) return;

      const user = getUser();
      const db = user?.database || prod?.database || "";
      const previousFY = getPreviousFY(normalizedSelectedFY);
      const productForLookup = {
        ...prod,
        _id: id,
        openingFinancialYear: savedOpening.openingFinancialYear,
      };

      setPreviousYearOpening((prev) => ({
        ...prev,
        loading: true,
        previousFY,
      }));

      try {
        let found = findPreviousClosingInSnapshot(
          db,
          previousFY,
          productForLookup,
          id,
        );

        if (!found) {
          const dbParam = `${db}?financialYear=${encodeURIComponent(
            previousFY,
          )}`;
          const res = await _Get(WareHouse_Closing_Stock, dbParam);
          const rows = normalizeClosingRowsFromResponse(res);
          found = findPreviousClosingInApiRows(rows, productForLookup, id);
        }

        if (found) {
          setPreviousYearOpening({
            loading: false,
            qty: found.qty,
            rate: found.rate,
            source: found.source,
            previousFY,
          });
        } else {
          setPreviousYearOpening({
            loading: false,
            qty: "",
            rate: "",
            source: `Previous FY closing not found (${previousFY})`,
            previousFY,
          });
        }
      } catch (e) {
        console.error("Unable to load previous FY closing for product", e);
        setPreviousYearOpening({
          loading: false,
          qty: "",
          rate: "",
          source: `Previous FY closing not found (${previousFY})`,
          previousFY,
        });
      }
    };

    loadPreviousOpening();
  }, [
    isOpeningYearMismatch,
    normalizedSelectedFY,
    prod.Product_Title,
    prod.warehouse,
    prod.database,
    savedOpening.openingFinancialYear,
    id,
  ]);

  // ===== Derived count =====
  const countInfo = useMemo(() => {
    const req = [
      prod.openingFinancialYear,
      prod.warehouse,
      prod.category,
      prod.Product_Title,
      prod.HSN_Code,
      prod.GSTRate,
      prod.MIN_stockalert,
      prod.primaryUnit,
      prod.Opening_Stock,
      prod.openingRate,
      prod.Purchase_Rate,
      prod.status,
    ];

    const done = req.filter(Boolean).length;
    return `${done}/${req.length} required`;
  }, [prod]);

  // ===== Category =====
  const onCategorySelect = (val) => {
    onProd("category", val);

    const c = categories.find((x) => x?.name === val);

    setSubcats(Array.isArray(c?.subcategories) ? c.subcategories : []);
    onProd("SubCategory", "");
  };

  // ===== Units =====
  const onPickUnit = (field, idOrName) => {
    const u = units.find(
      (x) => x?.unitId === idOrName || x?.unitName === idOrName,
    );

    onProd(field, u?.unitName || idOrName || "");
  };

  // ===== Images =====
  const addImages = (files) => {
    const f = Array.from(files || []);
    if (!f.length) return;

    setNewFiles((prev) => [...prev, ...f]);

    const readers = f.map(
      (file) =>
        new Promise((res) => {
          const r = new FileReader();
          r.onload = (ev) => res(ev.target.result);
          r.readAsDataURL(file);
        }),
    );

    Promise.all(readers).then((previews) => {
      setNewPreviews((p) => [...p, ...previews]);
    });
  };

  const removeNewImage = (idx) => {
    setNewFiles((p) => p.filter((_, i) => i !== idx));
    setNewPreviews((p) => p.filter((_, i) => i !== idx));
  };

  const removeExistingImageLocal = (idx) => {
    setExistingImages((p) => p.filter((_, i) => i !== idx));
  };

  // ===== Validation =====
  const validate = () => {
    const e = {};

    if (!prod.openingFinancialYear)
      e.openingFinancialYear = "Select opening financial year";
    if (!prod.warehouse) e.warehouse = "Select a warehouse";
    if (!prod.category) e.category = "Select a category";
    if (!prod.Product_Title) e.Product_Title = "Enter product name";
    if (!prod.HSN_Code) e.HSN_Code = "Enter HSN";
    if (!prod.GSTRate) e.GSTRate = "Enter GST";
    if (!prod.MIN_stockalert) e.MIN_stockalert = "Enter min stock";
    if (!prod.primaryUnit) e.primaryUnit = "Select primary unit";
    if (!prod.Opening_Stock) e.Opening_Stock = "Enter opening stock";
    if (!prod.openingRate) e.openingRate = "Enter opening rate";
    if (!prod.Purchase_Rate) e.Purchase_Rate = "Enter purchase rate";
    if (!prod.status) e.status = "Select status";
    if (prod.secondaryUnit && !prod.secondarySize)
      e.secondarySize = "Enter secondary qty";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ===== Submit =====
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);

    const u = getUser();
    const fd = new FormData();

    const payload = {
      database: u?.database || "",
      openingFinancialYear: normalizeFinancialYear(
        isOpeningYearMismatch
          ? savedOpening.openingFinancialYear || currentFY
          : prod.openingFinancialYear || currentFY,
      ),
      category: prod.category,
      SubCategory: prod.SubCategory,
      productSection: prod.section || "",
      warehouse: prod.warehouse,
      Product_Title: (prod.Product_Title || "").toUpperCase(),
      Product_MRP: num(prod.Product_MRP),
      igstType: toB(prod.purchaseStatus),
      price: num(prod.Purchase_Rate),
      HSN_Code: prod.HSN_Code,
      GSTRate: num(prod.GSTRate),
      Product_Desc: prod.Product_Desc || "",
      gstPercentage: num(prod.GSTRate),
      primaryUnit: prod.primaryUnit,
      secondaryUnit: prod.secondaryUnit,
      secondarySize: prod.secondaryUnit ? num(prod.secondarySize) : "",
      Opening_Stock: num(
        isOpeningYearMismatch ? savedOpening.Opening_Stock : prod.Opening_Stock,
      ),
      openingRate: num(
        isOpeningYearMismatch ? savedOpening.openingRate : prod.openingRate,
      ),
      Purchase_Rate: num(prod.Purchase_Rate),
      MIN_stockalert: num(prod.MIN_stockalert),
      status: prod.status,
    };

    fd.append("created_by", u?._id || "");
    Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
    newFiles.forEach((f) => fd.append("files", f));

    try {
      const endpoint = trimTrailingSlash(Update_Product);
      const cleanId = trimLeadingSlash(id);
      const res = await _Put(endpoint, cleanId, fd);

      setSaving(false);

      if (res?.status) {
        swal("Success", "Product Details Updated Successfully", "success");
        navigate(-1);
      } else {
        swal("Error", res?.message || "Update failed", "error");
      }
    } catch (err) {
      console.error(err);
      setSaving(false);
      swal("Error", "Something went wrong", "error");
    }
  };

  const sectionOptions = [
    { _id: "RAW_MATERIAL", name: "RAW MATERIAL", status: "Active" },
    ...sections.filter((s) => (s?.status || "Active") === "Active"),
  ];

  return (
    <>
      <Row className="ep-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="ep-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Edit Product <span className="count-pill">{countInfo}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <Button
                outline
                color="secondary"
                style={{ height: 32 }}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card className="ep-card">
            <CardBody>
              <Form onSubmit={onSubmit}>
                <Row>
                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Warehouse *</Label>
                    <Input
                      type="select"
                      value={prod.warehouse}
                      onChange={(e) => onProd("warehouse", e.target.value)}
                      style={{
                        borderColor: errors.warehouse ? "red" : "#ced4da",
                      }}
                      required
                    >
                      <option value="">-- Select Warehouse --</option>
                      {warehouses.map((w) => (
                        <option key={w?._id} value={w?._id}>
                          {w?.warehouseName}
                        </option>
                      ))}
                    </Input>
                    {errors.warehouse && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.warehouse}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Category *</Label>
                    <Input
                      type="select"
                      value={prod.category}
                      onChange={(e) => onCategorySelect(e.target.value)}
                      style={{
                        borderColor: errors.category ? "red" : "#ced4da",
                      }}
                      required
                    >
                      <option value="">-- Select Category --</option>
                      {categories.map((c) => (
                        <option key={c?._id} value={c?.name}>
                          {c?.name}
                        </option>
                      ))}
                    </Input>
                    {errors.category && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.category}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>SubCategory</Label>
                    <Input
                      type="select"
                      value={prod.SubCategory}
                      onChange={(e) => onProd("SubCategory", e.target.value)}
                    >
                      <option value="">-- Select SubCategory --</option>
                      {subcats.map((s) => (
                        <option key={s?._id} value={s?.name}>
                          {s?.name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Product Section</Label>
                    <Input
                      type="select"
                      value={prod.section}
                      onChange={(e) => onProd("section", e.target.value)}
                    >
                      <option value="">-- Select Section --</option>
                      {sectionOptions.map((sec) => (
                        <option key={sec?._id} value={sec?._id}>
                          {sec?.name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>GST Type *</Label>
                    <Input
                      type="select"
                      value={String(prod.purchaseStatus)}
                      onChange={(e) => onProd("purchaseStatus", e.target.value)}
                      required
                    >
                      <option value="">-- Select IGST Type --</option>
                      <option value="false">CGST/SGST</option>
                      <option value="true">IGST</option>
                    </Input>
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Product Name *</Label>
                    <Input
                      value={prod.Product_Title}
                      onChange={(e) =>
                        onProd("Product_Title", e.target.value.toUpperCase())
                      }
                      placeholder="Product title"
                      style={{
                        borderColor: errors.Product_Title ? "red" : "#ced4da",
                      }}
                      required
                    />
                    {errors.Product_Title && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.Product_Title}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>HSN *</Label>
                    <Input
                      value={prod.HSN_Code}
                      onChange={(e) => onProd("HSN_Code", e.target.value)}
                      placeholder="HSN Code"
                      style={{
                        borderColor: errors.HSN_Code ? "red" : "#ced4da",
                      }}
                      required
                    />
                    {errors.HSN_Code && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.HSN_Code}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>GST % *</Label>
                    <Input
                      type="number"
                      value={prod.GSTRate}
                      onChange={(e) => onProd("GSTRate", e.target.value)}
                      placeholder="GST percentage"
                      style={{
                        borderColor: errors.GSTRate ? "red" : "#ced4da",
                      }}
                      required
                    />
                    {errors.GSTRate && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.GSTRate}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Min Stock *</Label>
                    <Input
                      type="number"
                      value={prod.MIN_stockalert}
                      onChange={(e) => onProd("MIN_stockalert", e.target.value)}
                      placeholder="Minimum stock"
                      style={{
                        borderColor: errors.MIN_stockalert ? "red" : "#ced4da",
                      }}
                      required
                    />
                    {errors.MIN_stockalert && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.MIN_stockalert}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Primary Unit *</Label>
                    <Input
                      type="select"
                      value={prod.primaryUnit}
                      onChange={(e) =>
                        onPickUnit("primaryUnit", e.target.value)
                      }
                      style={{
                        borderColor: errors.primaryUnit ? "red" : "#ced4da",
                      }}
                      required
                    >
                      <option value="">-- Select Unit --</option>
                      {units.map((u) => (
                        <option key={u?.unitId} value={u?.unitName}>
                          {u?.unitName}
                        </option>
                      ))}
                    </Input>
                    {errors.primaryUnit && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.primaryUnit}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Secondary Unit</Label>
                    <Input
                      type="select"
                      value={prod.secondaryUnit}
                      onChange={(e) =>
                        onPickUnit("secondaryUnit", e.target.value)
                      }
                    >
                      <option value="">-- Select Unit --</option>
                      {units.map((u) => (
                        <option key={u?.unitId} value={u?.unitName}>
                          {u?.unitName}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {prod.secondaryUnit ? (
                    <Col lg="3" md="4" sm="6" className="mb-2">
                      <Label>Secondary Qty *</Label>
                      <Input
                        type="number"
                        value={prod.secondarySize}
                        onChange={(e) =>
                          onProd("secondarySize", e.target.value)
                        }
                        placeholder="Secondary quantity"
                        style={{
                          borderColor: errors.secondarySize ? "red" : "#ced4da",
                        }}
                        required
                      />
                      {errors.secondarySize && (
                        <div className="ep-small" style={{ color: "red" }}>
                          {errors.secondarySize}
                        </div>
                      )}
                    </Col>
                  ) : null}

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Product Desc</Label>
                    <Input
                      value={prod.Product_Desc || ""}
                      onChange={(e) => onProd("Product_Desc", e.target.value)}
                      placeholder="Description"
                    />
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Opening Financial Year *</Label>
                    <Input
                      type="select"
                      value={displayOpeningFinancialYear}
                      onChange={(e) => {
                        if (openingFieldsReadOnly) return;
                        onProd(
                          "openingFinancialYear",
                          normalizeFinancialYear(e.target.value),
                        );
                      }}
                      disabled={openingFieldsReadOnly}
                      style={{
                        borderColor: errors.openingFinancialYear
                          ? "red"
                          : "#ced4da",
                        background: openingFieldsReadOnly ? "#f8fafc" : "#fff",
                        fontWeight: openingFieldsReadOnly ? 800 : 400,
                      }}
                      required
                    >
                      {Array.from(
                        new Set(
                          [displayOpeningFinancialYear, ...fyOptions].filter(
                            Boolean,
                          ),
                        ),
                      ).map((fy) => (
                        <option key={fy} value={fy}>
                          {fy}
                        </option>
                      ))}
                    </Input>
                    {errors.openingFinancialYear && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.openingFinancialYear}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>
                      Opening Stock ({displayOpeningFinancialYear}) *
                    </Label>
                    <Input
                      type="number"
                      value={
                        previousYearOpening.loading ? "" : displayOpeningStock
                      }
                      onChange={(e) => {
                        if (openingFieldsReadOnly) return;
                        onProd("Opening_Stock", e.target.value);
                      }}
                      readOnly={openingFieldsReadOnly}
                      placeholder={
                        previousYearOpening.loading
                          ? "Loading previous closing..."
                          : "Opening stock"
                      }
                      style={{
                        borderColor: errors.Opening_Stock ? "red" : "#ced4da",
                        background: openingFieldsReadOnly ? "#f8fafc" : "#fff",
                        fontWeight: openingFieldsReadOnly ? 800 : 400,
                      }}
                      required
                    />
                    {errors.Opening_Stock && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.Opening_Stock}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>
                      Opening Rate ({displayOpeningFinancialYear}) *
                    </Label>
                    <Input
                      type="number"
                      value={
                        previousYearOpening.loading ? "" : displayOpeningRate
                      }
                      onChange={(e) => {
                        if (openingFieldsReadOnly) return;
                        onProd("openingRate", e.target.value);
                      }}
                      readOnly={openingFieldsReadOnly}
                      placeholder={
                        previousYearOpening.loading
                          ? "Loading previous closing..."
                          : "Opening rate"
                      }
                      style={{
                        borderColor: errors.openingRate ? "red" : "#ced4da",
                        background: openingFieldsReadOnly ? "#f8fafc" : "#fff",
                        fontWeight: openingFieldsReadOnly ? 800 : 400,
                      }}
                      required
                    />
                    {errors.openingRate && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.openingRate}
                      </div>
                    )}
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Opening Total</Label>
                    <Input
                      value={
                        previousYearOpening.loading ? "" : displayOpeningTotal
                      }
                      disabled
                      style={{ background: "#f8fafc", fontWeight: 800 }}
                    />
                  </Col>

                  {isOpeningYearMismatch && (
                    <Col lg="12" className="mb-2">
                      <div className="ep-opening-note">
                        Viewing FY {normalizedSelectedFY}. Product's saved
                        opening FY is {normalizedProductOpeningFY}. Opening
                        fields shown above are from{" "}
                        {previousYearOpening.source ||
                          `previous FY closing (${previousYearOpening.previousFY})`}
                        . On Save, the product will still save its original
                        opening data: FY {savedOpening.openingFinancialYear},
                        Qty {savedOpening.Opening_Stock}, Rate{" "}
                        {savedOpening.openingRate}.
                      </div>
                    </Col>
                  )}

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Product MRP</Label>
                    <Input
                      readOnly
                      value={prod.Product_MRP}
                      onChange={(e) => onProd("Product_MRP", e.target.value)}
                      placeholder="MRP"
                    />
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Landed Price (Basic Rate)</Label>
                    <Input
                      readOnly
                      value={prod.landedCost}
                      placeholder="Landed cost"
                    />
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Last Purchase Price</Label>
                    <Input readOnly value={lastPurchasePrice} placeholder="—" />
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Last Party Name</Label>
                    <Input readOnly value={lastPartyName} placeholder="—" />
                  </Col>

                  {existingImages.map((img, i) => (
                    <Col
                      key={`ex-${i}`}
                      lg="2"
                      md="3"
                      sm="4"
                      xs="6"
                      className="mb-2"
                      style={{ position: "relative" }}
                    >
                      <span
                        className="ep-del"
                        onClick={() => removeExistingImageLocal(i)}
                      >
                        <MdCancel size={22} color="red" />
                      </span>
                      <img
                        className="ep-img"
                        src={`${Image_URL}/Images/${img}`}
                        alt={`img-${i}`}
                      />
                    </Col>
                  ))}

                  <Col lg="12" className="mb-2">
                    <Label>Upload More Images</Label>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <label
                        style={{
                          display: "inline-grid",
                          placeItems: "center",
                          width: 26,
                          height: 26,
                          borderRadius: 14,
                          cursor: "pointer",
                          background: "#e2e8f0",
                        }}
                        title="Choose files"
                      >
                        <FaUpload />
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => addImages(e.target.files)}
                        />
                      </label>

                      <span className="ep-small">
                        You can select multiple images.
                      </span>
                    </div>

                    <Row className="mt-2">
                      {newPreviews.map((src, i) => (
                        <Col
                          key={`new-${i}`}
                          lg="2"
                          md="3"
                          sm="4"
                          xs="6"
                          className="mb-2"
                          style={{ position: "relative" }}
                        >
                          <span
                            className="ep-del"
                            onClick={() => removeNewImage(i)}
                          >
                            <MdCancel size={22} color="red" />
                          </span>
                          <img className="ep-img" src={src} alt={`new-${i}`} />
                        </Col>
                      ))}
                    </Row>
                  </Col>

                  <Col lg="3" md="4" sm="6" className="mb-2">
                    <Label>Status *</Label>
                    <div className="d-flex" style={{ gap: 16 }}>
                      <label
                        className="m-0 d-flex align-items-center"
                        style={{ gap: 6 }}
                      >
                        <Input
                          type="radio"
                          name="status"
                          checked={prod.status === "Active"}
                          onChange={() => onProd("status", "Active")}
                        />
                        <span>Active</span>
                      </label>

                      <label
                        className="m-0 d-flex align-items-center"
                        style={{ gap: 6 }}
                      >
                        <Input
                          type="radio"
                          name="status"
                          checked={prod.status === "Deactive"}
                          onChange={() => onProd("status", "Deactive")}
                        />
                        <span>Deactive</span>
                      </label>
                    </div>

                    {errors.status && (
                      <div className="ep-small" style={{ color: "red" }}>
                        {errors.status}
                      </div>
                    )}
                  </Col>
                </Row>

                <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                  <Button
                    color="secondary"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>

                  <Button className="ep-btn" type="submit" disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}
